import { HERO_MOMENT_XML } from '../../../shared/heroMomentArt.js'
import Seo from '../components/Seo'
import MarketingNav from '../components/MarketingNav'
import MarketingFooter from '../components/MarketingFooter'
import PageHero from '../components/ds/PageHero'
import Section from '../components/ds/Section'
import SectionHead from '../components/ds/SectionHead'
import CtaBand from '../components/ds/CtaBand'
import Button from '../components/ds/Button'
import FaqAccordion from '../components/ds/FaqAccordion'
import { buildFaqJsonLd } from '../lib/landingJsonLd'
import { dashboardUrl } from '../lib/dashboardUrl'

// One FAQ page, split by audience, because the two sides ask different things.
//
// ⚠️ The JSON-LD below carries BOTH lists, because both are rendered on this
// page. Google requires the structured data to match what a visitor can see, and
// <FaqAccordion> uses <details> so every answer is in the markup whether it is
// open or shut.
//
// ⛔ CLAIMS. The old version of this page said an agency adds "20% to 30%" and
// that shifts fill "often within the hour". Both were removed, not reworded:
// the first is a claim about somebody else’s pricing and the second is a
// performance claim, and neither has a source we can point at
// (docs/MARKETING-SITE-SPEC.md §8). Put them back only with evidence.

const WORK_FAQS = [
  { q: 'Am I employed by TempDash?',
    a: "No. You’re self employed. You choose every shift you take, and you’re never obliged to take one." },
  { q: 'When do I get paid?',
    a: 'The moment you clock out. Your pay lands straight in your TempDash wallet, ready to move to your bank in minutes. Overtime, a timesheet under review or a payment that needs checking can take up to 24 hours to clear.' },
  { q: 'What does it cost me?',
    a: 'Nothing to join and nothing to accept a shift.' },
  { q: 'What do I need before I can work?',
    a: 'Proof of who you are, your right to work in the UK, your professional registration, and the documents your role needs. About ten minutes, once.' },
  { q: 'Do I have to take a shift once I have seen it?',
    a: 'No. You look, and you accept the ones you want.' },
  { q: 'What if something goes wrong on the day?',
    a: "Tell us on the shift and we get you to the right person. You’re not left to sort it out with the workplace on your own." },
  { q: 'Do I need to invoice anyone?',
    a: 'No. Your hours come from your own clock in and clock out, and we handle the rest.' },
]

const COVER_FAQS = [
  { q: 'Is TempDash an agency?',
    a: 'No. TempDash is a marketplace. Workplaces contract directly with independent, self employed professionals under a contract for services. We are the software that matches you, runs the checks and moves the money.' },
  { q: 'Are the professionals checked?',
    a: 'Yes, before they can accept anything. Identity, right to work, professional registration and the documents their role needs, kept current rather than checked once.' },
  { q: 'How does pricing work?',
    a: "You see the full cost of a shift before you post it. There’s nothing to pay to have an account, nothing to post a shift, and nothing to pay for a shift no one accepts." },
  { q: 'When and how do we pay?',
    a: 'After the shift, by card or Direct Debit, on one bill with every shift itemised.' },
  { q: "What if someone doesn’t turn up?",
    a: "Tell us and we work the problem. Attendance is tracked on the day, so we usually know before you have to chase it." },
  { q: 'Can we manage more than one site?',
    a: 'Yes. One account can hold several workplaces, with shared billing and per site budgets if you want them.' },
  { q: "Do we have to run them through payroll?",
    a: "No. They’re self employed and we handle their pay, so there’s no invoice to process and no one joins your payroll." },
]

const ALL = [...WORK_FAQS, ...COVER_FAQS]

export default function Faq() {
  return (
    <div className="bg-canvas">
      <Seo
        title="Questions, answered"
        description="How TempDash works for people looking for shifts and for workplaces that need cover. Pay, checks, costs and what happens when something goes wrong."
        path="/faq"
        jsonLd={buildFaqJsonLd(ALL)}
      />
      <MarketingNav />

      {/* ⚠️ The ONE main landmark. Without it every <section> on the page
          sits outside any landmark and a screen reader user navigating by
          landmark cannot reach the content at all. The skip link in
          MarketingNav targets this id. */}
      <main id="main" tabIndex={-1} className="outline-none">

      <PageHero
        art={HERO_MOMENT_XML.feedback}
        width="text"
        title="Questions, answered."
        lead="The things people actually ask, on both sides. If yours isn’t here, a person will answer it."
      />

      <Section ground="canvas" width="text">
        <SectionHead eyebrow="Looking for work" size="md" title={<>For professionals</>} />
        <FaqAccordion items={WORK_FAQS} className="mt-8" />
      </Section>

      <Section ground="surface" width="text">
        <SectionHead eyebrow="Need cover" size="md" title={<>For workplaces</>} />
        <FaqAccordion items={COVER_FAQS} className="mt-8" />
      </Section>

      <CtaBand
        title="Still stuck?"
        lead="Email us and a person reads it. We answer in hours, not days."
      >
        <Button href="mailto:support@tempdash.app">Email support</Button>
        <Button href={dashboardUrl('/login')} variant="outline">Sign in</Button>
      </CtaBand>

      </main>

      <MarketingFooter />
    </div>
  )
}
